import React, { useEffect, useState } from "react";
import axios from "axios";
import { Play, Pause, Clock, Bell, Sparkles, ShoppingBag, ArrowUpRight, Heart, Download, Crown, Calendar } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { MERCH_URL, getMerchDrop } from "../lib/constants";
import { trackEvent } from "../lib/analytics";
import { useBookmarks } from "../hooks/useBookmarks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// "2026-07-07T04:00:00+00:00" → "Tue, Jul 7"
const formatDropDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
    });
};

// Human-friendly countdown: "3d · 14h", "22h · 15m", "45m", or "" if past/invalid.
const formatCountdown = (iso, now) => {
    if (!iso) return "";
    const target = new Date(iso).getTime();
    if (Number.isNaN(target)) return "";
    const diff = target - now;
    if (diff <= 0) return "";
    const totalMinutes = Math.floor(diff / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    if (days > 0) return `${days}d · ${hours}h`;
    if (hours > 0) return `${hours}h · ${minutes}m`;
    return `${minutes}m`;
};

// Tick every 60s — minute precision is enough for a "drops in Xd · Yh" pill.
const useNow = () => {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 60_000);
        return () => clearInterval(id);
    }, []);
    return now;
};

export const Episodes = () => {
    const [episodes, setEpisodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const { current, isPlaying, playEpisode, togglePlay } = usePlayer();
    const { isMarked, toggle } = useBookmarks();
    const now = useNow();

    useEffect(() => {
        let mounted = true;
        axios
            .get(`${API}/episodes`)
            .then((r) => mounted && setEpisodes(r.data || []))
            .catch(() => mounted && setEpisodes([]))
            .finally(() => mounted && setLoading(false));
        return () => {
            mounted = false;
        };
    }, []);

    const handlePlay = (ep) => {
        if (!ep.audio_url) {
            const join = document.getElementById("join");
            if (join) join.scrollIntoView({ behavior: "smooth" });
            return;
        }
        if (current?.id === ep.id) togglePlay();
        else playEpisode(ep);
    };

    return (
        <section id="episodes" data-testid="episodes-section" className="relative py-24 md:py-36">
            <div className="mx-auto max-w-7xl px-6 md:px-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                    <div>
                        <div className="eyebrow text-gold-deep mb-4">The episodes</div>
                        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-lilac-900">
                            Coming soon to your <em className="italic gold-text">becoming</em>.
                        </h2>
                        <p className="mt-5 text-lilac-700 max-w-xl leading-relaxed">
                            Episodes drop the moment Coach Melissa hits record. Subscribe below and
                            you'll be the first to press play.
                        </p>
                    </div>
                    <a
                        href="#join"
                        className="hidden md:inline-flex eyebrow text-lilac-700 hover:text-gold-deep transition-colors"
                    >
                        Subscribe for new drops →
                    </a>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div
                                key={i}
                                className="h-72 rounded-3xl bg-lilac-100/50 animate-pulse"
                            />
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {episodes.map((ep) => {
                            const hasAudio = !!ep.audio_url;
                            const isCurrent = current?.id === ep.id;
                            const playing = hasAudio && isCurrent && isPlaying;
                            const isScheduled = !ep.is_released && !!ep.release_at;
                            const dropLabel = isScheduled ? `Drops ${formatDropDate(ep.release_at)}` : "";
                            const countdown = isScheduled ? formatCountdown(ep.release_at, now) : "";
                            return (
                                <article
                                    key={ep.id}
                                    data-testid={`episode-card-${ep.number}`}
                                    className="group relative rounded-3xl bg-white border border-border overflow-hidden hover:shadow-[0_24px_64px_rgba(43,29,53,0.10)] transition-all"
                                >
                                    <div className="relative aspect-[16/10] overflow-hidden">
                                        <img
                                            src={ep.cover_url}
                                            alt={ep.title}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                                        />
                                        <div className="absolute inset-0 cover-fade" />
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggle("episodes", ep.id);
                                            }}
                                            data-testid={`bookmark-episode-${ep.number}`}
                                            aria-label={isMarked("episodes", ep.id) ? "Remove from My Becoming" : "Save to My Becoming"}
                                            className="absolute top-4 right-4 grid place-items-center w-9 h-9 rounded-full bg-white/90 backdrop-blur text-lilac-900 hover:bg-white transition-colors"
                                        >
                                            <Heart
                                                className={`w-4 h-4 ${isMarked("episodes", ep.id) ? "text-gold-deep" : "text-lilac-700"}`}
                                                fill={isMarked("episodes", ep.id) ? "currentColor" : "none"}
                                            />
                                        </button>
                                        <div className="absolute top-4 left-4 flex flex-wrap items-center gap-2 max-w-[calc(100%-3.5rem)]">
                                            <span className="eyebrow text-white/95 bg-black/25 backdrop-blur px-3 py-1 rounded-full">
                                                {ep.theme}
                                            </span>
                                            {ep.number === 6 && (
                                                <span
                                                    data-testid={`episode-finale-badge-${ep.number}`}
                                                    className="eyebrow text-white bg-gold/95 px-3 py-1 rounded-full flex items-center gap-1.5 shadow-[0_6px_18px_rgba(212,175,55,0.45)]"
                                                >
                                                    <Crown className="w-3 h-3" /> Season 1 Finale
                                                </span>
                                            )}
                                            {!hasAudio && ep.number !== 6 && (
                                                <span
                                                    data-testid={`episode-drops-${ep.number}`}
                                                    className="eyebrow text-lilac-900 bg-gold/95 px-3 py-1 rounded-full flex items-center gap-1.5"
                                                >
                                                    {isScheduled ? (
                                                        <>
                                                            <Calendar className="w-3 h-3" /> {dropLabel}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Sparkles className="w-3 h-3" /> Coming Soon
                                                        </>
                                                    )}
                                                </span>
                                            )}
                                            {!hasAudio && ep.number === 6 && (
                                                <span
                                                    data-testid={`episode-drops-${ep.number}`}
                                                    className="eyebrow text-lilac-900 bg-white/90 px-3 py-1 rounded-full flex items-center gap-1.5"
                                                >
                                                    {isScheduled ? (
                                                        <>
                                                            <Calendar className="w-3 h-3" /> {dropLabel}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Sparkles className="w-3 h-3" /> Coming Soon
                                                        </>
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-white">
                                            <span className="font-display text-sm">
                                                Episode {String(ep.number).padStart(2, "0")}
                                            </span>
                                            {hasAudio && ep.duration && (
                                                <span className="text-xs flex items-center gap-1.5">
                                                    <Clock className="w-3.5 h-3.5" /> {ep.duration}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        <h3 className="font-display text-xl md:text-2xl leading-snug text-lilac-900 line-clamp-2">
                                            {ep.title}
                                        </h3>
                                        <p className="mt-3 text-sm text-lilac-700 leading-relaxed line-clamp-3">
                                            {ep.description}
                                        </p>
                                        <div className="mt-6 flex items-center justify-between">
                                            <button
                                                data-testid={`play-episode-${ep.number}`}
                                                onClick={() => handlePlay(ep)}
                                                className={`inline-flex items-center gap-2 rounded-full h-11 pl-3 pr-5 text-sm font-medium transition-colors ${
                                                    !hasAudio
                                                        ? "bg-lilac-100 text-lilac-900 hover:bg-lilac-200"
                                                        : playing
                                                        ? "bg-lilac-900 text-white"
                                                        : "bg-gold text-white hover:bg-gold-deep"
                                                }`}
                                            >
                                                <span className={`grid place-items-center w-7 h-7 rounded-full ${!hasAudio ? "bg-white" : "bg-white/20"}`}>
                                                    {!hasAudio ? (
                                                        <Bell className="w-3.5 h-3.5 text-gold-deep" />
                                                    ) : playing ? (
                                                        <Pause className="w-3.5 h-3.5" fill="currentColor" />
                                                    ) : (
                                                        <Play className="w-3.5 h-3.5" fill="currentColor" />
                                                    )}
                                                </span>
                                                {!hasAudio ? "Notify Me" : playing ? "Pause" : "Listen"}
                                            </button>
                                            <span className="eyebrow text-lilac-700" data-testid={`episode-state-${ep.number}`}>
                                                {!hasAudio
                                                    ? (isScheduled
                                                        ? (countdown ? `Drops in ${countdown}` : dropLabel)
                                                        : "Coming soon")
                                                    : isCurrent
                                                    ? "Now playing"
                                                    : "Tap to play"}
                                            </span>
                                        </div>

                                        {/* Companion freebie (PDF worksheet, etc.) */}
                                        {ep.freebie_url && (
                                            <a
                                                href={
                                                    ep.freebie_url.startsWith("http")
                                                        ? ep.freebie_url
                                                        : `${process.env.REACT_APP_BACKEND_URL}${ep.freebie_url}`
                                                }
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid={`episode-freebie-${ep.number}`}
                                                className="mt-4 inline-flex items-center gap-2 text-[12px] font-semibold text-gold-deep hover:text-lilac-900 underline-offset-4 hover:underline decoration-gold/60 transition-colors"
                                            >
                                                <Download className="w-3.5 h-3.5" />
                                                {ep.freebie_label || "Free Companion Worksheet"}
                                            </a>
                                        )}
                                        {/* Featured Merch Drop */}
                                        {(() => {
                                            const drop = getMerchDrop(ep.theme);
                                            return (
                                                <a
                                                    href={MERCH_URL}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={() =>
                                                        trackEvent("episode_merch_clicked", {
                                                            episode: ep.number,
                                                            theme: ep.theme,
                                                        })
                                                    }
                                                    data-testid={`episode-merch-${ep.number}`}
                                                    className="mt-5 -mx-6 -mb-6 px-6 py-3 bg-gradient-to-r from-lilac-50 to-[#FAF5FC] border-t border-lilac-100 flex items-center gap-3 text-xs hover:from-lilac-100 hover:to-lilac-50 transition-colors group/merch"
                                                >
                                                    <span className="grid place-items-center w-7 h-7 rounded-full bg-gold/15 text-gold-deep shrink-0">
                                                        <ShoppingBag className="w-3.5 h-3.5" />
                                                    </span>
                                                    <span className="flex-1 min-w-0 text-lilac-900 leading-tight">
                                                        Pair with the{" "}
                                                        <span className="font-medium italic" style={{ color: "#B8902A" }}>
                                                            {drop.title}
                                                        </span>
                                                    </span>
                                                    <ArrowUpRight className="w-3.5 h-3.5 text-lilac-700 group-hover/merch:translate-x-0.5 group-hover/merch:-translate-y-0.5 transition-transform" />
                                                </a>
                                            );
                                        })()}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
};
